import { supabase } from "./supabase";
import type { User } from "@supabase/supabase-js";

export type LicenseStatus = "license_personal" | "license_none" | "deactivated";

export type Profile = {
  id: string;
  full_name: string | null;
  avatar_url: string | null;
  license_status: LicenseStatus;
  total_points: number;
  onboarding_complete: boolean;
};

export type AccessStatus = {
  user: User;
  profile: Profile;
  is_deactivated: boolean;
  needs_subscribe: boolean;
  can_study: boolean;
};

/**
 * Read the current user's profile via the my_profile() SECURITY DEFINER
 * RPC (the post-v34 authoritative path). Falls back to a direct table
 * read, then to defaults so the gate can act sensibly instead of
 * looping the user back to /login.
 */
/** Wrap any promise with a hard timeout — returns null after `ms`. */
function withTimeout<T>(p: PromiseLike<T>, ms: number, label: string): Promise<T | null> {
  return Promise.race([
    Promise.resolve(p).then(v => v as T | null),
    new Promise<null>(res => setTimeout(() => {
      console.warn(`[access] ${label} timed out after ${ms}ms`);
      res(null);
    }, ms))
  ]);
}

async function readProfile(userId: string): Promise<Profile> {
  // Path 1: RPC (bypasses RLS).
  try {
    const res = await withTimeout(supabase.rpc("my_profile"), 2500, "my_profile RPC");
    if (res && !res.error) {
      const data = res.data;
      if (Array.isArray(data) && data.length > 0) return normaliseProfile(userId, data[0]);
      if (data && !Array.isArray(data)) return normaliseProfile(userId, data);
    }
  } catch (e) { console.warn("[access] my_profile RPC threw", e); }

  // Path 2: table select.
  try {
    const res = await withTimeout(
      supabase.from("profiles")
        .select("id, full_name, avatar_url, license_status, total_points, onboarding_complete")
        .eq("id", userId)
        .maybeSingle(),
      2500, "profiles table read"
    );
    if (res && !res.error && res.data) return normaliseProfile(userId, res.data);
  } catch (e) { console.warn("[access] profile table read threw", e); }

  // Path 3: self-heal then retry RPC.
  try { await withTimeout(supabase.rpc("ensure_profile"), 2000, "ensure_profile"); } catch {}
  try { await withTimeout(supabase.from("profiles").insert({ id: userId }), 2000, "insert profile"); } catch {}
  try {
    const res = await withTimeout(supabase.rpc("my_profile"), 2000, "my_profile retry");
    if (res && Array.isArray(res.data) && res.data.length > 0) return normaliseProfile(userId, res.data[0]);
  } catch {}

  // Defaults — least-privilege so worker / gates default-deny.
  return {
    id: userId,
    full_name: null,
    avatar_url: null,
    license_status: "license_none",
    total_points: 0,
    onboarding_complete: false
  };
}

function normaliseProfile(userId: string, raw: any): Profile {
  return {
    id:                  raw.id ?? userId,
    full_name:           raw.full_name ?? null,
    avatar_url:          raw.avatar_url ?? null,
    license_status:      (raw.license_status as LicenseStatus) ?? "license_none",
    total_points:        Number(raw.total_points) || 0,
    onboarding_complete: !!raw.onboarding_complete
  };
}

export async function getAccessStatus(): Promise<AccessStatus | null> {
  const res = await withTimeout(supabase.auth.getUser(), 2500, "auth.getUser");
  const user = res?.data?.user;
  if (!user) return null;

  const profile = await readProfile(user.id);

  return {
    user,
    profile,
    is_deactivated:  profile.license_status === "deactivated",
    needs_subscribe: profile.license_status === "license_none",
    can_study:       profile.license_status === "license_personal"
  };
}

export function humaniseAuthError(err: unknown): string {
  const msg = (err as Error)?.message || "Something went wrong.";
  if (/invalid login credentials/i.test(msg)) return "Wrong email or password.";
  if (/email not confirmed/i.test(msg))       return "Please confirm your email first.";
  if (/already.*registered/i.test(msg))       return "There's already an account with that email.";
  if (/password.*weak|password.*short/i.test(msg)) return "Pick a longer, harder-to-guess password.";
  if (/rate limit/i.test(msg))                return "Too many attempts. Try again in a minute.";
  return msg;
}
